import { Routes } from "@/app/routes.ts";
import { useMobileMenuContext } from "@/components/layouts/MobileMenuProvider.tsx";
import { Button, Div, LinkButton, Text } from "@hopper-ui/components";
import { DeleteIcon } from "@hopper-ui/icons";
import { DeleteConversationModal, useDeleteConversationMutation, useFormatDate, useIsTouchDevice, useSharedTranslation, useTracking } from "@workleap-ai/shared";
import { MixpanelEvent, type Conversation } from "@workleap-ai/shared/types";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import "./ConversationHistoryCategoryItem.css";

interface Props {
    conversation: Conversation;
}

export const ConversationHistoryCategoryItem = ({ conversation }: Props) => {
    const { tShared } = useSharedTranslation();
    const navigate = useNavigate();
    const { toggleMobileMenu, isMobileMenuOpen } = useMobileMenuContext();
    const formatDate = useFormatDate();
    const isTouchDevice = useIsTouchDevice();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deletingConversation, setDeletingConversation] = useState<boolean>(false);
    const { mutate } = useDeleteConversationMutation(() => {
        setTimeout(() => {
            setDeletingConversation(false);
        }, 300);
    });
    const { trackEvent } = useTracking();
    const { title, updatedAt, conversationId } = conversation;

    const conversationTitle = useMemo(() => {
        if (!title) {
            return tShared("newConversation");
        }

        return title;
    }, [tShared, title]);

    const detailedUpdatedAtDate = formatDate(updatedAt);
    const isActive = location.pathname === Routes.Conversation(conversationId);

    const onConversationPress = useCallback(() => {
        trackEvent(MixpanelEvent.ChatHistoryItemLoaded, { "Conversation Id": conversationId });

        toggleMobileMenu();
    }, [conversationId, toggleMobileMenu, trackEvent]);

    const onDeletePress = useCallback(() => {
        setIsModalOpen(true);
        trackEvent(MixpanelEvent.ButtonClicked, { Location: "Assistant Conversation Delete Button", Trigger: "Delete Conversation" });
    }, [trackEvent]);

    const deleteConversation = useCallback(() => {
        setIsModalOpen(false);

        if (deletingConversation) {
            return;
        }

        setDeletingConversation(true);
        mutate({ conversationId: conversationId });

        if (isActive) {
            isMobileMenuOpen && toggleMobileMenu();
            navigate(Routes.Home());
        }
    }, [deletingConversation, mutate, conversationId, isActive, isMobileMenuOpen, toggleMobileMenu, navigate]);

    return (
        <Div
            position="relative"
            title={`${conversationTitle} - ${detailedUpdatedAtDate}`}
            className={classNames("wai-conversation-history-item", { "wai-conversation-history-item-deleting": deletingConversation })}
        >
            <LinkButton
                size="sm"
                onPress={onConversationPress}
                key={conversationId}
                href={Routes.Conversation(conversationId)}
                variant="ghost-secondary"
                paddingX="inset-sm"
                justifyContent="flex-start"
                width="100%"
                textAlign="left"
                color="neutral"
                backgroundColorHover="neutral-weakest-hover"
                backgroundColor={isActive ? "neutral-weakest-hover" : "transparent"}
                className={classNames("wai-conversation-history-item-trigger", { "wai-conversation-history-item-active": isActive })}
            >
                <Text
                    display="block"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    UNSAFE_lineHeight={1.5}
                >
                    {conversationTitle}
                </Text>
            </LinkButton>
            <Button
                size="xs"
                variant="ghost-secondary"
                position="absolute"
                right="0"
                top="0.25rem"
                zIndex={1}
                opacity={isTouchDevice ? 1 : 0}
                className="wai-conversation-history-delete-button"
                backgroundColor={isTouchDevice ? (isActive ? "neutral-weak" : "neutral-weakest") : "neutral-weak"}
                aria-label={tShared("deleteConversation")}
                border="none"
                onPress={onDeletePress}
            >
                <DeleteIcon fill="neutral" opacity="0.6" />
            </Button>
            <DeleteConversationModal
                isOpen={isModalOpen}
                conversationTitle={conversationTitle}
                onClose={() => setIsModalOpen(false)}
                onConfirm={deleteConversation}
            />
        </Div>
    );
};
